import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// ── "View as teacher" ─────────────────────────────────────────────
//
// An admin can open the teacher portal AS a named teacher, from the
// Teacher Portal Preview on Examination Settings. It works by carrying
// ?view_as=<user_id> in the URL.
//
// Three things must happen on EVERY page of the portal, and missing any
// one of them breaks the preview in a way that looks like a different
// bug entirely:
//
//   1. Send it to the API (`viewAsParams`), or the page answers about
//      the admin instead of the teacher. That is how a preview ended up
//      showing "14 of your 14 classes" — a super-admin's register duty
//      is every class in the school — under a teacher's name, and how a
//      teacher assigned one subject was shown every subject in the
//      class on their score sheet.
//
//   2. Keep it in the URL when navigating (`withViewAs`), because
//      TeacherGuard lets an admin into /teacher-portal/* ONLY while the
//      param is present. Navigating without it bounces to /dashboard,
//      which reads on screen as a click that flashes and goes back.
//
//   3. Treat the preview as READ-ONLY (`viewAsId` as the flag). Marks
//      carry no record of who saved them, so an admin looking around
//      would overwrite the teacher's own work with no trace.
//
// One hook, so a page added later gets all three by default.
export default function useViewAs() {
  const loc = useLocation();
  const viewAsId = new URLSearchParams(loc.search).get('view_as');
  // Memoised on the id, not rebuilt per render. A fresh object every
  // render propagates through the useCallback/useEffect dependency
  // chains that fetch data — loadMeta changes identity, the effect that
  // calls it re-runs, state updates, and the page fetches in a loop.
  return useMemo(() => ({
    viewAsId,
    // Spread into an axios config: { params: { ...viewAsParams } }
    viewAsParams: viewAsId ? { view_as: viewAsId } : undefined,
    // Wrap any in-portal path before navigating to it.
    withViewAs: (path) =>
      viewAsId ? `${path}?view_as=${encodeURIComponent(viewAsId)}` : path,
  }), [viewAsId]);
}
